// src/pages/TenantAccounts.tsx
//
// Cross-tenant ADMIN ACCOUNT management for operators.
//
// Distinct from UserManagement.tsx, which manages operator accounts for this
// portal itself (registry_super_admin_users). This page manages the people who
// log in to TENANT portals — the accounts that used to live in Zitadel's
// console. Password resets, session revocation, 2FA resets and disabling had no
// home in CITS while an external IdP owned credentials; now they have to.
//
// Everything here acts on the REGISTRY (auth_user / auth_session), never on a
// tenant database, so it keeps working when a tenant DB is down — which is
// frequently the moment an operator needs it most.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  UserMinusIcon,
  UsersIcon, TrashIcon} from '@heroicons/react/24/outline';
import api from '../api/axios';
import CompanyAccounts from './tenantAccounts/CompanyAccounts';

interface TenantAccount {
  id: string;
  email: string;
  name: string | null;
  role: string | null;
  banned: boolean;
  twoFactorEnabled: boolean;
  createdAt: string | null;
  company_id: string | null;
  company_name: string | null;
}

const pill = {
  green: 'bg-emerald-100 text-emerald-700',
  slate: 'bg-slate-100 text-slate-600',
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-800',
} as const;

function Pill({ tone, children }: { tone: keyof typeof pill; children: React.ReactNode }) {
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${pill[tone]}`}>{children}</span>;
}

export default function TenantAccounts() {
  const [accounts, setAccounts] = useState<TenantAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  // By company: employees, roles and field-app/web access per company (needs
  // the tenant DB). All logins: every web login in the registry, which keeps
  // working when a tenant DB is down.
  const [view, setView] = useState<'company' | 'logins'>('company');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/super-admin/account/users', { params: { limit: 500 } });
      setAccounts(res.data?.users ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not load accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return accounts.filter((a) => {
      if (status === 'no2fa' && a.twoFactorEnabled) return false;
      if (status === 'disabled' && !a.banned) return false;
      if (!needle) return true;
      return (
        a.email.toLowerCase().includes(needle) ||
        (a.name || '').toLowerCase().includes(needle) ||
        (a.company_name || '').toLowerCase().includes(needle)
      );
    });
  }, [accounts, query, status]);

  const stats = useMemo(() => ({
    total: accounts.length,
    with2fa: accounts.filter((a) => a.twoFactorEnabled).length,
    disabled: accounts.filter((a) => a.banned).length,
    companies: new Set(accounts.map((a) => a.company_id).filter(Boolean)).size,
  }), [accounts]);

  /**
   * Delete an identity outright, freeing its email.
   *
   * Separate from act(): this is a DELETE, and the server answers 409 with the
   * companies the login still administers rather than removing live access
   * behind the operator's back. That second confirm is the whole safeguard, so
   * it must be an explicit choice, not a retry the UI makes on its own.
   */
  async function removeAccount(account: TenantAccount) {
    if (!window.confirm(
      `Delete the login for ${account.email}?\n\n` +
      `This frees the email address so it can be used again. It cannot be undone. ` +
      `To stop someone signing in without losing the account, use Disable instead.`
    )) return;
    setBusyId(account.id);
    try {
      const url = `/super-admin/account/users/${encodeURIComponent(account.id)}`;
      try {
        await api.delete(url);
      } catch (err: any) {
        if (err?.response?.status !== 409) throw err;
        const companies: string[] = err.response.data?.companies ?? [];
        if (!window.confirm(
          `${account.email} still administers ${companies.join(', ')}.\n\n` +
          `Deleting removes that access as well. Continue?`
        )) { setBusyId(null); return; }
        await api.delete(`${url}?force=true`);
      }
      setNotice({ ok: true, text: `Deleted ${account.email}. The address is free to use again.` });
      await load();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      setNotice({
        ok: false,
        text:
          code === 'reauth_required'
            ? 'Re-authentication required — sign in again before deleting an account.'
            : err?.response?.data?.message || 'Could not delete the account.',
      });
    } finally {
      setBusyId(null);
    }
  }

  async function act(account: TenantAccount, action: string, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(account.id);
    try {
      await api.post(`/super-admin/account/users/${encodeURIComponent(account.id)}/${action}`);
      setNotice({ ok: true, text: `${action.replace(/-/g, ' ')} completed for ${account.email}.` });
      await load();
    } catch (err: any) {
      // The step-up gate answers 401 reauth_required rather than 403, so the UI
      // can distinguish "prove it's you again" from "you may not do this".
      const code = err?.response?.data?.code;
      setNotice({
        ok: false,
        text:
          code === 'reauth_required'
            ? 'Re-authentication required — sign in again before performing this action.'
            : code === 'two_factor_required'
              ? 'Enrol two-factor on your operator account before using these controls.'
              : err?.response?.data?.message || `Could not ${action.replace(/-/g, ' ')}.`,
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
              <UsersIcon className="h-7 w-7 text-blue-600" />
              Tenant accounts
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Every company's employees, roles and sign-in access. All logins works from the registry alone, so it keeps working when a tenant database is down.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <ArrowPathIcon className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 pt-5">
        <div className="flex gap-1 border-b border-slate-200" role="tablist">
          {([['company', 'By company'], ['logins', 'All logins']] as const).map(([k, label]) => (
            <button
              key={k}
              role="tab"
              aria-selected={view === k}
              onClick={() => setView(k)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                view === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'company' ? (
        <div className="mx-auto max-w-7xl px-6 py-6">
          <CompanyAccounts />
        </div>
      ) : (
      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: 'Accounts', value: stats.total },
            { label: 'With 2FA', value: stats.with2fa },
            { label: 'Disabled', value: stats.disabled },
            { label: 'Companies', value: stats.companies },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{s.label}</div>
              <div className="text-2xl font-bold tabular-nums text-slate-900">{s.value}</div>
            </div>
          ))}
        </div>

        {notice && (
          <div
            role="status"
            className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
              notice.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {notice.text}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex h-9 min-w-[220px] flex-1 items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />
            <label className="sr-only" htmlFor="ta-q">Search accounts</label>
            <input
              id="ta-q"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email, name or company..."
              className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
            />
          </div>
          <label className="sr-only" htmlFor="ta-status">Status</label>
          <select
            id="ta-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-lg border border-slate-300 bg-white px-2.5 text-sm"
          >
            <option value="all">Any status</option>
            <option value="no2fa">No 2FA</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {loading ? (
            <div className="px-4 py-16 text-center text-sm text-slate-500">Loading accounts…</div>
          ) : error ? (
            <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-red-700">
              <ExclamationTriangleIcon className="h-5 w-5" /> {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-slate-500">No accounts match.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-left">
                <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">Account</th>
                    <th className="px-4 py-2.5">Company</th>
                    <th className="px-4 py-2.5">2FA</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-4 py-2.5">
                        <div className="text-sm font-semibold text-slate-900">{a.name || a.email}</div>
                        <div className="text-xs text-slate-500">{a.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-slate-600">{a.company_name || '—'}</td>
                      <td className="px-4 py-2.5">
                        {a.twoFactorEnabled ? <Pill tone="green">Enabled</Pill> : <Pill tone="slate">None</Pill>}
                      </td>
                      <td className="px-4 py-2.5">
                        {a.banned ? <Pill tone="red">Disabled</Pill> : <Pill tone="green">Active</Pill>}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <button
                            type="button"
                            disabled={busyId === a.id}
                            onClick={() => act(a, 'send-reset')}
                            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"
                          >
                            <KeyIcon className="h-3.5 w-3.5" /> Reset
                          </button>
                          <button
                            type="button"
                            disabled={busyId === a.id}
                            onClick={() => act(a, 'revoke-sessions', `Sign ${a.email} out of every device?`)}
                            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"
                          >
                            <ArrowPathIcon className="h-3.5 w-3.5" /> Sign out
                          </button>
                          {a.twoFactorEnabled && (
                            <button
                              type="button"
                              disabled={busyId === a.id}
                              onClick={() => act(a, 'reset-2fa', `Remove two-factor for ${a.email}? They will have to enrol again.`)}
                              className="flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 disabled:opacity-50"
                            >
                              <ShieldExclamationIcon className="h-3.5 w-3.5" /> Reset 2FA
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busyId === a.id}
                            onClick={() =>
                              act(
                                a,
                                a.banned ? 'enable' : 'disable',
                                a.banned ? undefined : `Disable ${a.email}? They will be signed out and unable to sign in.`
                              )
                            }
                            className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold disabled:opacity-50 ${
                              a.banned
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                                : 'border-red-300 bg-red-50 text-red-700'
                            }`}
                          >
                            {a.banned ? <ShieldCheckIcon className="h-3.5 w-3.5" /> : <UserMinusIcon className="h-3.5 w-3.5" />}
                            {a.banned ? 'Enable' : 'Disable'}
                          </button>
                          <button
                            type="button"
                            disabled={busyId === a.id}
                            onClick={() => removeAccount(a)}
                            title="Delete this login and free its email address. Irreversible — use Disable to just block sign-in."
                            className="flex items-center gap-1 rounded-lg border border-red-400 bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            <TrashIcon className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
